import { analyticsClient } from './analyticsClient';

export const QI_INTERNAL_STORAGE_KEY = 'qi_internal';

type InternalTrafficState = {
  tagged: boolean;
};

function readInternalTrafficState(): InternalTrafficState {
  let search = '';
  try {
    search = window.location.search;
  } catch {
    return { tagged: false };
  }

  let internalParam: string | null = null;
  try {
    internalParam = new URLSearchParams(search).get('internal');
  } catch {
    return { tagged: false };
  }

  if (internalParam === '1') {
    try {
      localStorage.setItem(QI_INTERNAL_STORAGE_KEY, '1');
    } catch {
      // URL explicitly requests internal tagging; honor it for this load even if storage fails.
    }
    return { tagged: true };
  }

  if (internalParam === '0') {
    try {
      localStorage.removeItem(QI_INTERNAL_STORAGE_KEY);
    } catch {
      return { tagged: false };
    }
    return { tagged: false };
  }

  try {
    const stored = localStorage.getItem(QI_INTERNAL_STORAGE_KEY);
    return {
      tagged: stored === '1',
    };
  } catch {
    return { tagged: false };
  }
}

/** Reads ?internal= and `qi_internal` storage; true when internal traffic tagging is active. */
export function resolveInternalFlag(): boolean {
  return readInternalTrafficState().tagged;
}

function shouldTagInternalTraffic(search: string): boolean {
  let internalParam: string | null = null;
  try {
    internalParam = new URLSearchParams(search).get('internal');
  } catch {
    return false;
  }

  if (internalParam === '0') return false;
  if (internalParam === '1') return true;

  try {
    return localStorage.getItem(QI_INTERNAL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Removes `internal` from the current URL without navigation. Preserves other
 * query params, hash, and history state. No-op when history is unavailable.
 */
export function stripInternalQueryParamFromUrl(): void {
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }

  if (!url.searchParams.has('internal')) return;

  url.searchParams.delete('internal');
  const nextSearch = url.searchParams.toString();
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ''}${url.hash}`;

  try {
    if (typeof window.history?.replaceState !== 'function') return;
    window.history.replaceState(window.history.state, '', nextUrl);
  } catch {
    // ignore environments without history API
  }
}

function searchHasInternalParam(search: string): boolean {
  try {
    return new URLSearchParams(search).has('internal');
  } catch {
    return false;
  }
}

/**
 * Registers `internal: true` for PostHog filtering when `internal=1` or qi_internal
 * is persisted. Strips `internal` from the URL when that query param is present.
 */
export function applyInternalTrafficFlag(search: string): void {
  if (searchHasInternalParam(search)) {
    try {
      const internalParam = new URLSearchParams(search).get('internal');
      if (internalParam === '0') {
        localStorage.removeItem(QI_INTERNAL_STORAGE_KEY);
      }
    } catch {
      // ignore storage failures
    }
    stripInternalQueryParamFromUrl();
  }

  if (!shouldTagInternalTraffic(search)) return;

  if (!process.env.REACT_APP_POSTHOG_KEY) return;

  try {
    analyticsClient.register({ internal: true });
  } catch {
    // analytics may be unavailable when disabled
  }
}

// Persist or clear qi_internal from the landing URL before analytics init runs.
readInternalTrafficState();
